/**
 * Decrypt data using AES-GCM
 * Matches the Chrome extension encryption.js implementation
 * @param {string} encryptedBase64 - Encrypted data in base64
 * @param {string} saltBase64 - Salt in base64
 * @param {string} ivBase64 - IV in base64
 * @param {string} password - Password for decryption (shareId)
 * @returns {Promise<Object>} Decrypted data (will be JSON parsed)
 */
export async function decryptData(encryptedBase64, saltBase64, ivBase64, password) {
  try {
    // Convert from base64
    const encrypted = Uint8Array.from(atob(encryptedBase64), c => c.charCodeAt(0));
    const salt = Uint8Array.from(atob(saltBase64), c => c.charCodeAt(0));
    const iv = Uint8Array.from(atob(ivBase64), c => c.charCodeAt(0));

    // Derive key from password
    const encoder = new TextEncoder();
    const keyMaterial = await crypto.subtle.importKey(
      "raw",
      encoder.encode(password),
      "PBKDF2",
      false,
      ["deriveBits", "deriveKey"]
    );

    const key = await crypto.subtle.deriveKey(
      {
        name: "PBKDF2",
        salt: salt,
        iterations: 100000,
        hash: "SHA-256",
      },
      keyMaterial,
      { name: "AES-GCM", length: 256 },
      true,
      ["encrypt", "decrypt"]
    );

    // Decrypt the data
    const decrypted = await crypto.subtle.decrypt(
      {
        name: "AES-GCM",
        iv: iv,
      },
      key,
      encrypted
    );

    // Convert decrypted ArrayBuffer to string and parse JSON
    const decoder = new TextDecoder();
    const decryptedText = decoder.decode(decrypted);
    return JSON.parse(decryptedText);
  } catch (error) {
    console.error('Decryption error:', error);
    throw new Error('Failed to decrypt data: ' + error.message);
  }
}
